import { ChangeEvent, DragEvent, FC, useRef, useState } from "react";
import { Box, Icon, Text, VStack } from "@chakra-ui/react";
import { useTranslation } from "react-i18next";

import { UploadIcon } from "../icons";

interface IHomeIntroUploaderProps {
  onFiles: (files: File[]) => void;
  isLoading?: boolean;
}

const HomeIntroUploader: FC<IHomeIntroUploaderProps> = ({
  onFiles,
  isLoading,
}) => {
  const { t } = useTranslation();
  const inputRef = useRef<HTMLInputElement>(null);
  const [isDragging, setDragging] = useState(false);

  const handleFiles = (files: File[]) => {
    if (!isLoading && files.length > 0) {
      onFiles(files);
    }
  };

  const handleBrowseClick = () => {
    if (inputRef.current && !isLoading) {
      inputRef.current.value = "";
      inputRef.current.click();
    }
  };

  const handleInputChange = (e: ChangeEvent<HTMLInputElement>) => {
    handleFiles(Array.from(e.target.files || []));
  };

  const handleDrop = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setDragging(false);
    handleFiles(Array.from(e.dataTransfer.files));
  };

  return (
    <Box
      position="relative"
      w="full"
      p="1"
      borderRadius="xl"
      bgGradient="linear(to-r, brand.500, brand.700)"
      boxShadow="0 10px 30px rgba(0, 0, 0, 0.25)"
    >
      <input
        ref={inputRef}
        type="file"
        multiple
        onChange={handleInputChange}
        style={{ display: "none" }}
      />

      <VStack
        role="button"
        tabIndex={0}
        spacing="3"
        align="center"
        justify="center"
        minH={{ base: "180px", md: "220px" }}
        px={{ base: "5", md: "8" }}
        py={{ base: "8", md: "10" }}
        borderWidth="1px"
        borderStyle="dashed"
        borderColor={isDragging ? "brand.400" : "gray.600"}
        borderRadius="lg"
        bg={isDragging ? "brand.900" : "gray.900"}
        textAlign="center"
        cursor={isLoading ? "wait" : "pointer"}
        opacity={isLoading ? "0.7" : "1"}
        transition="background 150ms ease-out, border-color 150ms ease-out"
        onClick={handleBrowseClick}
        onDrop={handleDrop}
        onDragEnter={(e) => {
          e.preventDefault();
          setDragging(true);
        }}
        onDragOver={(e) => e.preventDefault()}
        onDragLeave={() => setDragging(false)}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            handleBrowseClick();
          }
        }}
      >
        <Icon
          as={UploadIcon}
          w="8"
          h="auto"
          color={isDragging ? "brand.300" : "brand.500"}
        />

        <Box>
          <Text fontWeight="semibold" fontSize="xl">
            {isLoading
              ? t("home.intro.uploader.loading")
              : t("home.intro.uploader.title")}
          </Text>
          <Text mt="1.5" fontSize="sm" color="gray.300">
            {t("home.intro.uploader.description")}{" "}
            <Text as="span" color="brand.500" fontWeight="medium">
              {t("home.intro.uploader.action")}
            </Text>
          </Text>
        </Box>
      </VStack>
    </Box>
  );
};

export default HomeIntroUploader;
